import React from 'react';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ActionMenu from '../action-menu/ActionMenu';
import { getTaskColorClass } from './scheduleUtils';
import { TaskCompletionButton } from './TaskCompletionButton';
import { getTaskScheduleLabel, TaskLabelContext } from './taskScheduleLabel';
import { PlannerTaskInfo } from './types';
import { useTimelineView } from './TimelineViewContext';

export const TimelineTaskCard: React.FC<{
    dateContext?: TaskLabelContext; onBeforeDelete?: () => void; onBeforeEdit?: () => void; onBeforeOpen?: () => void;
    taskInfo: PlannerTaskInfo;
}> = ({
    dateContext, onBeforeDelete, onBeforeEdit, onBeforeOpen, taskInfo,
}) => {
    const { task, folderId, collectionId } = taskInfo;
    const { t, i18n } = useTranslation();
    const {
        getCollectionName, getFolderName, onDeleteTask, onEditTask, onOpenTask, onToggleComplete,
    } = useTimelineView();
    const folder = getFolderName(folderId);
    const source = folder.trim().toLocaleLowerCase(i18n.language) === 'general'
        ? getCollectionName(collectionId) : `${folder} / ${getCollectionName(collectionId)}`;
    return (
        <div className={`timeline-task ${getTaskColorClass(task)} ${task.completed ? 'completed' : ''}`}
            data-timeline-task-id={task.id} onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); onBeforeOpen?.(); onOpenTask(taskInfo); }}
            onDoubleClick={(event) => { event.stopPropagation(); onBeforeEdit?.(); onEditTask(task, collectionId); }}>
            <TaskCompletionButton task={task} collectionId={collectionId} onToggleComplete={onToggleComplete} />
            <div className="timeline-task-info">
                <span className="task-title">{task.title}</span>
                <span className="task-interval">
                    {getTaskScheduleLabel(
                        task, i18n.language, new Date().getFullYear(), dateContext, {
                            deadline: t('addTask.deadline'), startsAt: t('dailyPlanner.startsAt'),
                            startsIn: t('dailyPlanner.startsIn'), startsOn: t('dailyPlanner.startsOn'),
                        }
                    )}
                </span>
                <span className="task-source">{source}</span>
            </div>
            <ActionMenu icon={undefined} buttonStyle={{}} actions={[
                {
                    label: t('dailyPlanner.editTask'), icon: faPen,
                    callback: () => { onBeforeEdit?.(); onEditTask(task, collectionId); },
                },
                { label: t('dailyPlanner.deleteTask'), icon: faTrash, callback: () => {
                    onBeforeDelete?.();
                    onDeleteTask(task.id, collectionId);
                } },
            ]} />
        </div>
    );
};

export const TimelineOverviewTask: React.FC<{ taskInfo: PlannerTaskInfo }> = ({ taskInfo }) => {
    const { task, collectionId } = taskInfo;
    const { t, i18n } = useTranslation();
    const { onEditTask, onOpenTask } = useTimelineView();
    return (
        <button type="button"
            className={`timeline-overview-task ${getTaskColorClass(task)} ${task.completed ? 'completed' : ''}`}
            data-timeline-task-id={task.id}
            onClick={(event) => { event.stopPropagation(); onOpenTask(taskInfo); }}
            onDoubleClick={(event) => { event.stopPropagation(); onEditTask(task, collectionId); }} title={task.title}>
            <span className="timeline-overview-task-marker" aria-hidden="true"></span>
            <span className="timeline-overview-task-title">{task.title}</span>
            <span className="timeline-overview-task-range">
                {getTaskScheduleLabel(
                    task, i18n.language, new Date().getFullYear(), undefined, {
                        deadline: t('addTask.deadline'), startsAt: t('dailyPlanner.startsAt'),
                        startsIn: t('dailyPlanner.startsIn'), startsOn: t('dailyPlanner.startsOn'),
                    }
                )}
            </span>
        </button>
    );
};
